'use client'

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Users,
  Calendar,
  BarChart3,
  PieChart,
  Download,
  Filter,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus,
  Target,
  ShoppingCart,
  RotateCcw
} from 'lucide-react'

interface AnalyticsData {
  totalItems: number
  totalValue: number
  itemsAdded: number
  itemsSold: number
  itemsReturned: number
  revenue: number
  profit: number
  averageItemValue: number
  topCategories: Array<{
    name: string
    count: number
    value: number
  }>
  monthlyTrends: Array<{
    month: string
    items: number
    value: number
    revenue: number
  }>
  recentActivity: Array<{
    id: string
    action: string
    item: string
    value: number
    timestamp: string
  }>
}

const mockAnalytics: AnalyticsData = {
  totalItems: 1247,
  totalValue: 125430.50,
  itemsAdded: 45,
  itemsSold: 23,
  itemsReturned: 3,
  revenue: 8750.25,
  profit: 2100.75,
  averageItemValue: 100.58,
  topCategories: [
    { name: 'Electronics', count: 156, value: 45230.00 },
    { name: 'Clothing', count: 234, value: 32150.00 },
    { name: 'Books', count: 189, value: 8950.00 },
    { name: 'Home & Garden', count: 98, value: 15680.00 },
    { name: 'Sports', count: 67, value: 12340.00 }
  ],
  monthlyTrends: [
    { month: 'Jan', items: 45, value: 12500, revenue: 2500 },
    { month: 'Feb', items: 52, value: 15200, revenue: 3200 },
    { month: 'Mar', items: 38, value: 11800, revenue: 2100 },
    { month: 'Apr', items: 61, value: 18900, revenue: 4100 },
    { month: 'May', items: 47, value: 14200, revenue: 2900 },
    { month: 'Jun', items: 55, value: 16800, revenue: 3600 }
  ],
  recentActivity: [
    { id: '1', action: 'Item Sold', item: 'Vintage Camera', value: 250.00, timestamp: '2 hours ago' },
    { id: '2', action: 'Item Added', item: 'Designer Watch', value: 450.00, timestamp: '4 hours ago' },
    { id: '3', action: 'Item Returned', item: 'Leather Jacket', value: -89.99, timestamp: '6 hours ago' },
    { id: '4', action: 'Item Sold', item: 'Antique Vase', value: 125.00, timestamp: '8 hours ago' },
    { id: '5', action: 'Item Added', item: 'Vintage Book', value: 35.00, timestamp: '1 day ago' }
  ]
}

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState<AnalyticsData>(mockAnalytics)
  const [dateRange, setDateRange] = useState('30d')
  const [loading, setLoading] = useState(false)

  const handleRefresh = async () => {
    setLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    setLoading(false)
  }

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return <ArrowUp className="h-4 w-4 text-green-500" />
    if (current < previous) return <ArrowDown className="h-4 w-4 text-red-500" />
    return <Minus className="h-4 w-4 text-gray-500" />
  }

  const getTrendColor = (current: number, previous: number) => {
    if (current > previous) return 'text-green-500'
    if (current < previous) return 'text-red-500'
    return 'text-gray-500'
  }

  return (
    <div
      style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        fontFamily: 'Rajdhani, sans-serif'
      }}
    >
      {/* Page Title */}
      <h1
        style={{
          fontSize: '48px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          marginBottom: '32px',
          textShadow: '0 0 30px rgba(251, 146, 60, 0.7), 0 0 60px rgba(251, 146, 60, 0.4)',
          background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        Analytics Dashboard
      </h1>

      <div className="space-y-6">
        {/* Header Controls */}
        <Card
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(251, 146, 60, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(251, 146, 60, 0.2)',
            boxShadow: '0 0 30px rgba(251, 146, 60, 0.2)'
          }}
        >
          {/* Corner accent */}
          <div
            className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))'
            }}
          />

          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="p-3 rounded-lg border"
                  style={{
                    background: 'rgba(251, 146, 60, 0.1)',
                    borderColor: 'rgba(251, 146, 60, 0.3)',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <TrendingUp className="h-7 w-7 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-wide">Date Range</p>
                  <p className="text-base text-white font-medium">
                    {dateRange === '7d' && 'Last 7 days'}
                    {dateRange === '30d' && 'Last 30 days'}
                    {dateRange === '90d' && 'Last 90 days'}
                    {dateRange === '1y' && 'Last year'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 rounded-lg text-sm text-white font-medium"
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(251, 146, 60, 0.3)',
                    backdropFilter: 'blur(8px)'
                  }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
                <Button
                  onClick={handleRefresh}
                  disabled={loading}
                  variant="secondary"
                  size="sm"
                  className="hover:bg-orange-500/20 border-orange-500/30"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Items Card */}
          <Card
            className="group relative overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.1)'
            }}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom right, transparent, transparent, rgba(59, 130, 246, 0.1))'
              }}
            />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                  Total Items
                </CardTitle>
                <div
                  className="p-2 rounded-lg border"
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <Package className="h-5 w-5 text-blue-400" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white mb-1">
                {analytics.totalItems.toLocaleString()}
              </p>
              <p className="text-sm text-gray-400">
                +{analytics.itemsAdded} added this month
              </p>
            </CardContent>
          </Card>

          {/* Total Value Card */}
          <Card
            className="group relative overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(251, 146, 60, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(251, 146, 60, 0.2)',
              boxShadow: '0 0 20px rgba(251, 146, 60, 0.1)'
            }}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))'
              }}
            />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                  Total Value
                </CardTitle>
                <div
                  className="p-2 rounded-lg border"
                  style={{
                    background: 'rgba(251, 146, 60, 0.1)',
                    borderColor: 'rgba(251, 146, 60, 0.3)'
                  }}
                >
                  <DollarSign className="h-5 w-5 text-orange-400" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white mb-1">
                ${analytics.totalValue.toLocaleString()}
              </p>
              <p className="text-sm text-gray-400">
                Avg: ${analytics.averageItemValue.toFixed(2)} per item
              </p>
            </CardContent>
          </Card>

          {/* Revenue Card */}
          <Card
            className="group relative overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(34, 197, 94, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
              boxShadow: '0 0 20px rgba(34, 197, 94, 0.1)'
            }}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom right, transparent, transparent, rgba(34, 197, 94, 0.1))'
              }}
            />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                  Revenue
                </CardTitle>
                <div
                  className="p-2 rounded-lg border"
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderColor: 'rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <TrendingUp className="h-5 w-5 text-green-400" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white mb-1">
                ${analytics.revenue.toLocaleString()}
              </p>
              <p className="text-sm text-gray-400">
                {analytics.itemsSold} items sold
              </p>
            </CardContent>
          </Card>

          {/* Profit Card */}
          <Card
            className="group relative overflow-hidden transition-all duration-300 hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(168, 85, 247, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              boxShadow: '0 0 20px rgba(168, 85, 247, 0.1)'
            }}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.1))'
              }}
            />
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400 uppercase tracking-wide">
                  Profit
                </CardTitle>
                <div
                  className="p-2 rounded-lg border"
                  style={{
                    background: 'rgba(168, 85, 247, 0.1)',
                    borderColor: 'rgba(168, 85, 247, 0.3)'
                  }}
                >
                  <Target className="h-5 w-5 text-purple-400" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-white mb-1">
                ${analytics.profit.toLocaleString()}
              </p>
              <p className="text-sm text-gray-400">
                {((analytics.profit / analytics.revenue) * 100).toFixed(1)}% margin
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Top Categories */}
        <Card
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(236, 72, 153, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(236, 72, 153, 0.2)',
            boxShadow: '0 0 30px rgba(236, 72, 153, 0.2)'
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(236, 72, 153, 0.1))'
            }}
          />

          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg border"
                style={{
                  background: 'rgba(236, 72, 153, 0.1)',
                  borderColor: 'rgba(236, 72, 153, 0.3)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <PieChart className="h-7 w-7 text-pink-400" />
              </div>
              <CardTitle className="text-lg font-semibold text-pink-200">
                Top Categories
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-3">
              {analytics.topCategories.map((category, index) => (
                <div
                  key={category.name}
                  className="flex items-center justify-between p-3 rounded-lg transition-all duration-200"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(236, 72, 153, 0.1)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="secondary"
                      className="text-xs"
                      style={{
                        background: 'rgba(236, 72, 153, 0.2)',
                        border: '1px solid rgba(236, 72, 153, 0.3)',
                        color: '#f9a8d4'
                      }}
                    >
                      #{index + 1}
                    </Badge>
                    <span className="text-white font-medium">{category.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">{category.count} items</p>
                    <p className="text-sm text-pink-300">${category.value.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Trends */}
        <Card
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(6, 182, 212, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(6, 182, 212, 0.2)',
            boxShadow: '0 0 30px rgba(6, 182, 212, 0.2)'
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(6, 182, 212, 0.1))'
            }}
          />

          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg border"
                style={{
                  background: 'rgba(6, 182, 212, 0.1)',
                  borderColor: 'rgba(6, 182, 212, 0.3)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <BarChart3 className="h-7 w-7 text-cyan-400" />
              </div>
              <CardTitle className="text-lg font-semibold text-cyan-200">
                Monthly Trends
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-3">
              {analytics.monthlyTrends.map((trend, index) => (
                <div
                  key={trend.month}
                  className="flex items-center justify-between p-3 rounded-lg transition-all duration-200"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(6, 182, 212, 0.1)'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-white font-bold text-base uppercase" style={{ minWidth: '40px' }}>
                      {trend.month}
                    </span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-400">
                        <span className="text-white font-medium">{trend.items}</span> items
                      </span>
                      <span className="text-gray-400">
                        Value: <span className="text-cyan-300 font-medium">${trend.value.toLocaleString()}</span>
                      </span>
                      <span className="text-gray-400">
                        Revenue: <span className="text-green-300 font-medium">${trend.revenue.toLocaleString()}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {index > 0 && getTrendIcon(trend.items, analytics.monthlyTrends[index - 1].items)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(251, 146, 60, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(251, 146, 60, 0.2)',
            boxShadow: '0 0 30px rgba(251, 146, 60, 0.2)'
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))'
            }}
          />

          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg border"
                style={{
                  background: 'rgba(251, 146, 60, 0.1)',
                  borderColor: 'rgba(251, 146, 60, 0.3)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Calendar className="h-7 w-7 text-orange-400" />
              </div>
              <CardTitle className="text-lg font-semibold text-orange-200">
                Recent Activity
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent>
            <div className="space-y-3">
              {analytics.recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 rounded-lg transition-all duration-200"
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(251, 146, 60, 0.1)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {activity.action === 'Item Sold' && (
                        <div
                          className="p-2 rounded-lg border"
                          style={{
                            background: 'rgba(34, 197, 94, 0.1)',
                            borderColor: 'rgba(34, 197, 94, 0.3)'
                          }}
                        >
                          <ShoppingCart className="h-4 w-4 text-green-400" />
                        </div>
                      )}
                      {activity.action === 'Item Added' && (
                        <div
                          className="p-2 rounded-lg border"
                          style={{
                            background: 'rgba(59, 130, 246, 0.1)',
                            borderColor: 'rgba(59, 130, 246, 0.3)'
                          }}
                        >
                          <Package className="h-4 w-4 text-blue-400" />
                        </div>
                      )}
                      {activity.action === 'Item Returned' && (
                        <div
                          className="p-2 rounded-lg border"
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            borderColor: 'rgba(239, 68, 68, 0.3)'
                          }}
                        >
                          <RotateCcw className="h-4 w-4 text-red-400" />
                        </div>
                      )}
                      <span className="text-white font-medium">{activity.action}</span>
                    </div>
                    <span className="text-gray-300">{activity.item}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-bold text-base ${
                      activity.value > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {activity.value > 0 ? '+' : ''}${activity.value.toFixed(2)}
                    </span>
                    <span className="text-sm text-gray-400 uppercase tracking-wide" style={{ minWidth: '70px' }}>
                      {activity.timestamp}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Export Options */}
        <Card
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(168, 85, 247, 0.1) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            boxShadow: '0 0 30px rgba(168, 85, 247, 0.2)'
          }}
        >
          <div
            className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(168, 85, 247, 0.1))'
            }}
          />

          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="p-3 rounded-lg border"
                style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  borderColor: 'rgba(168, 85, 247, 0.3)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Download className="h-7 w-7 text-purple-400" />
              </div>
              <CardTitle className="text-lg font-semibold text-purple-200">
                Export Reports
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="secondary"
                className="hover:bg-purple-500/20 border-purple-500/30"
                style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="secondary"
                className="hover:bg-purple-500/20 border-purple-500/30"
                style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Button
                variant="secondary"
                className="hover:bg-purple-500/20 border-purple-500/30"
                style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.3)'
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
